import React from 'react';
import { withTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import {
    Row,
    Card,
    CardBody,
    CardTitle,
    CardSubtitle,
    Container,
    NavLink,
    Spinner,
    Col,
} from 'reactstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faMoneyCheck,
    faFileInvoice,
    faFileInvoiceDollar,
    faFileContract,
    faUser,
    faToolbox,
    faWarehouse,
    faPhoneVolume,
    faTasks,
    faAddressBook,
    faComments,
    faCalendar,
    faFileLines,
    faFlag,
} from '@fortawesome/free-solid-svg-icons';
import { Link } from '@s20/components/common/Link';
import {
    getIsAuthenticated, getTenantSettings, getTimesheetNotifications, getUserIsAdmin,
} from '@s20/selectors/features/common.selectors';
import { getUserHasSelfDispatchAccess, getPermissionStrings } from '@s20/selectors/features/permissions.selectors';
import { Can } from './components/Can';
import { AppNavPaths } from './components/Common';
import { CustomerPortal } from './components/dashboard/CustomerPortal';
import { OnCallAvailability } from './components/dashboard/OnCallAvailability';

class Dashboard extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            notification: null,
        };
    }

    render() {
        const {
            hasSelfDispatchingAccess, notifications: timesheetNotificationCount, tenantSettings, permissions, t,
        } = this.props;

        const hasCustomerPortal = permissions.includes('customer_portal.all');

        if (hasCustomerPortal) {
            return <CustomerPortal />;
        }

        const billing = t('common:billing');
        const hasIndiv = permissions.includes(
            'payroll.viewindividual',
        );
        const hasEdit = permissions.includes('payroll.edit');

        const canAccessViewDelegates =
            ['employee.view', 'multi_tenant.all'].every((p) => permissions.includes(p));

        const projectsEnabled = tenantSettings.useContractProjects === 2 || (
            tenantSettings.useContractProjects === 1
            && tenantSettings.allowRentalChargeTypes
        );

        const canAccessQuickSales = permissions.includes('quick_sale.view', 'quick_sale.create') &&
            Boolean(tenantSettings.enableQuickSales) === true;

        if (!tenantSettings) {
            return (
                <div className="confirmationContainer pt-2 d-flex flex-row align-items-center justify-content-center">
                    <Spinner tag="div" />
                </div>
            );
        }
        const areas = [];
        if (tenantSettings.enableEstimate) {
            areas.push({
                icon: faFileContract,
                url: AppNavPaths.Estimates,
                name: 'Estimates',
                permission: 'contract',
                action: 'view',
            });
        }

        areas.push({
            icon: faFileContract,
            url: AppNavPaths.Contracts,
            name: 'Contracts',
            permission: 'contract',
            action: 'view',
        });

        if (projectsEnabled) {
            areas.push({
                icon: faFileLines,
                url: AppNavPaths.Projects,
                name: 'Projects',
                permission: 'job',
                action: 'view',
            });
        }

        areas.push(
            ...[
                {
                    iconStack: {
                        primary: 'fa-calendar-day',
                        secondary: 'fa-file-contract',
                    },
                    url: AppNavPaths.JobView,
                    name: 'Daily Jobs View',
                    permission: 'job_daily_view',
                    action: 'view',
                },
                {
                    iconStack: {
                        primary: 'fa-calendar-day',
                        secondary: 'fa-portrait',
                    },
                    url: AppNavPaths.EmployeeDailyRoster,
                    name: 'Roster View',
                    permission: 'employee_daily_roster',
                    action: 'view',
                },
                {
                    icon: faPhoneVolume,
                    url: AppNavPaths.Dispatch,
                    name: 'Dispatch',
                    permission: 'dispatch',
                    action: 'view',
                },
                {
                    icon: faTasks,
                    url: AppNavPaths.DispatchPlusDispatch,
                    name: 'Dispatch Plus',
                    permission: 'dispatch_plus',
                    action: 'view',
                },
                {
                    icon: faFlag,
                    url: AppNavPaths.DispatchIssues,
                    name: 'Dispatch Issues',
                    permission: 'dispatch_issues',
                    action: 'view',
                },
                {
                    icon: faUser,
                    url: AppNavPaths.Employees,
                    name: 'Employees',
                    permission: 'employee',
                    action: 'view',
                }]);

        if (canAccessViewDelegates) {
            areas.push({
                icon: faUser,
                url: AppNavPaths.Delegates,
                name: 'Delegates',
                permission: 'employee',
                action: 'view',
            });
        }

        /*
         This My Calendar tile is only shown if the user is NOT in a Field Group, as per ROX-2150
         Reverted the changes as APPHELP-1436, requeted that all the field group users want to have access to My calendar.
        */

        areas.push({
            icon: faCalendar,
            url: AppNavPaths.AssignmentCalendar,
            name: t('nav:myCalendar'),
            permission: 'calendar',
            action: 'view',
        });

        areas.push(
            ...[
                {
                    icon: faToolbox,
                    url: AppNavPaths.Equipment,
                    name: 'Equipment',
                    permission: 'equipment',
                    action: 'view',
                },
                {
                    icon: faWarehouse,
                    url: AppNavPaths.Organizations,
                    name: 'Organizations',
                    permission: 'company',
                    action: 'view',
                },
                {
                    iconStack: {
                        primary: 'fa-calendar',
                        secondary: 'fa-briefcase',
                    },
                    url: AppNavPaths.CalendarJobs,
                    name: 'Job Calendar',
                    permission: 'jobcalendar',
                    action: 'view',
                },
            ]);

        areas.push(
            ...[
                {
                    icon: faTasks,
                    url: AppNavPaths.TimesheetManagement,
                    name: 'Timesheet Management',
                    permission: 'dispatch_review',
                    action: 'view',
                },
                {
                    icon: faTasks,
                    url: AppNavPaths.ChargeView,
                    name: 'Charge View',
                    permission: 'charge_view',
                    action: 'view',
                },
                // { icon: faBusinessTime, url: AppNavPaths.TimeOffRequests, name: "Time Off Requests", permission: "timeoff", action: "view" },
            ],
        );

        if (tenantSettings.contactScreenEnabled) {
            areas.push({
                icon: faAddressBook,
                url: AppNavPaths.Contacts,
                name: 'Contacts',
                permission: 'location_contact',
                action: 'view',
            });
        }

        if (tenantSettings.contactActivitiesEnabled) {
            areas.push({
                icon: faComments,
                url: AppNavPaths.ContactActivities,
                name: 'Contact Activities',
                permission: 'contactactivity',
                action: 'view',
            });
        }

        if (tenantSettings.payrollEnabled && hasEdit) {
            areas.push({
                icon: faMoneyCheck,
                url: AppNavPaths.PayrollManagement,
                name: 'Payroll: Charges',
                permission: 'payroll',
                action: 'view',
            });
        }


        // 2022-10-10 - M. Nicol - Done to prevent multiple "Payroll: Pay Categories" tiles
        // from being displayed.  If the user has "viewinidividual" permissions, use that in the "Can" check
        // in the render method.  Otherwise, use "view".  If the user has neither, "view" will be used, and the
        // "Can" check will be false, resulting in the expected behavior of not showing the tile.
        // There may be a better way using more sophisticated CASL conditions like $in:
        // https://casl.js.org/v5/en/guide/conditions-in-depth

        if (tenantSettings.payrollEnabled) {
            const payCatUrl = hasIndiv
                ? AppNavPaths.PayrollIndividual
                : AppNavPaths.Payroll;
            const payCatAction = hasIndiv ? 'viewindividual' : 'view';

            areas.push({
                icon: faMoneyCheck,
                url: payCatUrl,
                name: 'Payroll: Pay Categories',
                permission: 'payroll',
                action: payCatAction,
            });
        }

        if (tenantSettings.billingEnabled) {
            areas.push({
                icon: faFileInvoice,
                url: AppNavPaths.Billing,
                name: billing,
                permission: 'billing',
                action: 'view',
            });
        }

        if (
            tenantSettings.billingEnabled
            && tenantSettings.invoicingEnabled
        ) {
            areas.push({
                icon: faComments,
                url: AppNavPaths.BillingIssues,
                name: 'Billing: Issues',
                permission: 'billing_issues',
                action: 'view',
            });

            areas.push({
                icon: faFileInvoice,
                url: AppNavPaths.BillingCharge,
                name: 'Billing: Charges',
                permission: 'billing',
                action: 'view',
            });

            areas.push({
                icon: faFileInvoice,
                url: AppNavPaths.BillingChargeCategory,
                name: 'Billing: Charge Categories',
                permission: 'billing',
                action: 'view',
            });

            areas.push({
                icon: faFileInvoice,
                url: AppNavPaths.InvoiceGeneration,
                name: 'Invoice Generation',
                permission: 'billing',
                action: 'view',
            });

            areas.push({
                icon: faFileInvoiceDollar,
                url: AppNavPaths.Invoice,
                name: 'Invoices',
                permission: 'invoice',
                action: 'view',
            });

            if (
                tenantSettings.allowRentalChargeTypes && Boolean(tenantSettings.combineRentalNonRentalChargesOnRentalInvoices) !== true
            ) {
                areas.push({
                    icon: faFileInvoiceDollar,
                    url: AppNavPaths.RentalInvoice,
                    name: 'Rental Invoices',
                    permission: 'rental_invoice',
                    action: 'view',
                });
            }
        }

        if (hasSelfDispatchingAccess) {
            areas.push({
                iconStack: {
                    primary: 'fa-clock',
                    secondary: 'fa-plus-circle',
                    rounded: true,
                },
                url: AppNavPaths.NonFlaggingTimesheetNew,
                name: t('nav:nonFlaggingTimesheet'),
                permission: 'nonflaggingtimesheet',
                action: 'create',
            });
            if (tenantSettings.enableContractTimesheetCheckins) {
                areas.push({
                    iconStack: {
                        primary: 'fa-clock',
                        secondary: 'fa-clock-circle',
                        rounded: true,
                    },
                    url: AppNavPaths.TimeTracker,
                    name: t('nav:timeTracker'),
                    permission: 'nonflaggingtimesheet',
                    action: 'create',
                });
            }
        }

        if (hasSelfDispatchingAccess) {
            areas.push({
                iconStack: {
                    primary: 'fa-clock',
                    count:
                        timesheetNotificationCount > 0
                            ? timesheetNotificationCount
                            : null,
                    rounded: true,
                },
                url: AppNavPaths.EmployeeTimesheets,
                name: t('nav:myTimesheets'),
                permission: 'timesheet',
                action: 'view',
            });
        }

        if (canAccessQuickSales) {
            areas.push({
                iconStack: {
                    primary: 'fa-clock',
                    secondary: 'fa-plus-circle',
                    rounded: true,
                },
                url: AppNavPaths.QuickSales,
                name: 'Quick Sales',
                permission: 'quick_sale',
                action: 'create',
            });
        }

        return (
            <Container className="background-white">
                <OnCallAvailability />
                <Row className="dashboard-row">
                    {areas.map((a) => (
                        <Can
                            key={a.name}
                            do={a.action}
                            on={a.permission}
                        >
                            <Col>
                                <NavLink
                                    className="dashboard-link"
                                    tag={Link}
                                    to={a.url}
                                >
                                    <Card>
                                        <CardBody>
                                            <CardTitle className="d-flex flex-column align-items-center">
                                                {!a.iconStack && (
                                                    <FontAwesomeIcon
                                                        className="text-muted"
                                                        size="4x"
                                                        icon={a.icon}
                                                    />
                                                )}
                                                {!!a.iconStack && (
                                                    <span className="fa-stack fa-2x text-muted">
                                                        <i
                                                            className={`fas ${a.iconStack.primary} fa-stack-2x`}
                                                            data-count={
                                                                a.iconStack?.count
                                                            }
                                                        />
                                                        <i
                                                            className={`dashboard-stacked-icon fas ${a.iconStack
                                                                .secondary
                                                                } fa-stack-1x ml-3 mt-3${a.iconStack
                                                                    ?.rounded
                                                                    ? ' rounded'
                                                                    : ''
                                                                }`}
                                                        />
                                                    </span>
                                                )}
                                            </CardTitle>
                                            <CardSubtitle className="d-flex flex-column align-items-center">
                                                <h4 className="mt-1 pt-1 pb-1 text-center border-red-top-bottom">
                                                    {a.name}
                                                </h4>
                                            </CardSubtitle>
                                        </CardBody>
                                    </Card>
                                </NavLink>
                            </Col>
                        </Can>
                    ))}
                </Row>
            </Container>
        );
    }
}

const mapStateToProps = (state) => ({
    notifications: getTimesheetNotifications(state),
    hasSelfDispatchingAccess: getUserHasSelfDispatchAccess(state),
    isAuthenticated: getIsAuthenticated(state),
    tenantSettings: getTenantSettings(state),
    permissions: getPermissionStrings(state),
    isAdmin: getUserIsAdmin(state),
});

export default connect(mapStateToProps)(withTranslation()(Dashboard));
